import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Calendar } from './calendar';
import TasksListComponent from './tasks-list';
import { TasksProps } from './types';
import { getUserTasksVars } from './util';

export function TasksComponent(props: TasksProps) {
  const { featureId, type, workspaceId, options = {}, controller, t } = props;
  const savedUserViewState = getUserTasksVars(featureId);

  const [showCalendar, setShowCalendar] = useState(false);
  const [featureProps, setFeatureProps] = useState<{
    inputs?: Record<string, unknown>;
    outputs?: Record<string, EventListenerOrEventListenerObject>;
  }>({ inputs: options.inputs, outputs: options.outputs });

  const tasksListRef = useRef<HTMLDivElement>(null);
  const isPortal = props.options?.isPortal;

  const completeTask = useCallback(
    (taskId: string, done: boolean) => {
      const allowComplete =
        options.inputs &&
        typeof options.inputs.nimAllowedActions === 'object' &&
        options.inputs.nimAllowedActions &&
        'taskUpdate' in options.inputs.nimAllowedActions &&
        options.inputs.nimAllowedActions.taskUpdate;
      if (!allowComplete) return;
      controller.completeTask(taskId, done, workspaceId);
    },
    [workspaceId, options, controller]
  );

  const onChangeCalendarView = useCallback(
    ({ featureId: id, isCalendarEnabled }: { featureId: string; isCalendarEnabled: boolean }) => {
      if (id === featureId) {
        setShowCalendar(isCalendarEnabled);
      }
    },
    [featureId]
  );

  const onFeaturePropsChanged = useCallback(
    ({
      featureId: id,
      props,
    }: {
      featureId: string;
      props: {
        inputs?: Record<string, unknown>;
        outputs?: Record<string, EventListenerOrEventListenerObject>;
      };
    }) => {
      if (featureId === id) setFeatureProps(props);
    },
    [featureId]
  );

  useEffect(() => {
    controller.emitter.on('change-calendar-view', onChangeCalendarView);
    controller.emitter.on('feature-props-changed', onFeaturePropsChanged);
    return () => {
      controller.emitter.off('change-calendar-view', onChangeCalendarView);
      controller.emitter.off('feature-props-changed', onFeaturePropsChanged);
    };
  }, [controller, onChangeCalendarView, onFeaturePropsChanged]);

  return (
    <>
      <div ref={tasksListRef} className={classNames('feature-wrapper w-full', { 'calendar-view': showCalendar })}>
        <TasksListComponent
          {...{
            inputs: {
              ...featureProps.inputs,
              // TODO: временно отключаем пункт Rename для таск листа
              nimAllowedActions: { ...(featureProps.inputs?.nimAllowedActions || {}), taskListRenameMenuItem: false },
              nimShowDashboardButton: !isPortal,
              nimShowCalendarSwitch: true,
            },
            outputs: { ...featureProps.outputs },
          }}
        />
      </div>
      <div className={classNames('w-full pl-8 pr-6 pb-6', { hidden: !showCalendar })}>
        <div className="w-full h-full flex flex-col" data-gramm={false}>
          <Calendar
            featureId={featureId}
            workspaceId={workspaceId}
            tasks={[]}
            completeTask={completeTask}
            t={t}
            options={{
              ...options.calendar,
              initialView: savedUserViewState?.calendarView,
              allowCreateNewTask:
                options.showCreateTaskButtons &&
                Boolean(
                  featureProps.inputs &&
                    featureProps.inputs.nimAllowedActions &&
                    typeof featureProps.inputs.nimAllowedActions === 'object' &&
                    'taskCreate' in featureProps.inputs.nimAllowedActions &&
                    featureProps.inputs.nimAllowedActions.taskCreate
                ),
            }}
            themeColors={options.themeColors}
            themeStyle={options.themeStyle}
            controller={controller}
            type={type}
          />
        </div>
      </div>
    </>
  );
}
